import React from 'react'
import {
  Box,
  Button,
  Center,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Grid,
  GridItem,
  HStack,
  Icon,
  Input,
} from '@chakra-ui/react'

import { Controller, useFieldArray, useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { NEW_PROPERTY } from 'features/property/property.i18n'
import { fieldsLimitNumber, usePropertyResidentSaveButtonDisabled } from './hooks'
import { PropertyFormValues } from 'types/property.type'
import { preventSpecialCharacter } from 'utils/string-formatters'
import NumberFormat from 'react-number-format'
import { MdOutlineCancel } from 'react-icons/md'
import PropertyAddIcon from './icons/add-icon'
import { NEW_PROJECT } from 'features/vendor/projects/projects.i18n'

type InfoProps = {
  setNextTab: () => void
  onClose: () => void
}

export const AddResidentInfo = React.forwardRef((props: InfoProps, ref) => {
  const { t } = useTranslation()

  const {
    register,
    formState: { errors },
    control,
    setValue,
    setError,
    clearErrors,
  } = useFormContext<PropertyFormValues>()

  const {
    fields: residentFields,
    append: residentsAppend,
    remove: residentRemove,
  } = useFieldArray({ control, name: 'residents' })

  const isSaveButtonDisabled = usePropertyResidentSaveButtonDisabled(control as any)

  return (
    <Flex flexDir="column">
      <Box px="6" h="455px" overflow="auto">
        <Button
          fontSize="12px"
          fontWeight="500"
          variant="outline"
          colorScheme="brand"
          onClick={() =>
            residentsAppend({
              firstName: '',
              lastName: '',
              emailAddress: '',
              phoneNumber: '',
            })
          }
          mt={2}
          leftIcon={<PropertyAddIcon />}
        >
          Add New Row
        </Button>
        {residentFields.map((residents, index) => {
          return (
            <Box>
              <Grid templateColumns="repeat(4, 190px)" gap={'1rem 1rem'} py="3">
                <GridItem>
                  <FormControl isInvalid={!!errors?.residents?.[index]?.firstName}>
                    <FormLabel size="md">First Name</FormLabel>
                    <Input
                      id="contact"
                      {...register(`residents.${index}.firstName`, { required: 'This is required' })}
                      variant={'required-field'}
                      type="text"
                      onKeyPress={e => preventSpecialCharacter(e)}
                      onChange={e => {
                        setValue(`residents.${index}.firstName`, (e?.target as HTMLInputElement).value)
                        if ((e.target as HTMLInputElement).value?.length > fieldsLimitNumber) {
                          setError(`residents.${index}.firstName`, {
                            type: 'custom',
                            message: t(`${NEW_PROJECT}.fieldLimitReached`),
                          })
                        } else {
                          clearErrors(`residents.${index}.firstName`)
                        }
                      }}
                    />
                    <FormErrorMessage>{errors?.residents?.[index]?.firstName?.message}</FormErrorMessage>
                  </FormControl>
                </GridItem>

                <GridItem>
                  <FormControl isInvalid={!!errors?.residents?.[index]?.lastName}>
                    <FormLabel size="md">Last Name</FormLabel>
                    <Input
                      id="contact"
                      {...register(`residents.${index}.lastName`, { required: 'This is required' })}
                      variant={'required-field'}
                      type="text"
                      onKeyPress={e => preventSpecialCharacter(e)}
                      onChange={e => {
                        setValue(`residents.${index}.lastName`, (e?.target as HTMLInputElement).value)
                        if ((e.target as HTMLInputElement).value?.length > fieldsLimitNumber) {
                          setError(`residents.${index}.lastName`, {
                            type: 'custom',
                            message: t(`${NEW_PROJECT}.fieldLimitReached`),
                          })
                        } else {
                          clearErrors(`residents.${index}.lastName`)
                        }
                      }}
                    />
                    <FormErrorMessage>{errors?.residents?.[index]?.lastName?.message}</FormErrorMessage>
                  </FormControl>
                </GridItem>

                <GridItem>
                  <FormControl isInvalid={!!errors?.residents?.[index]?.emailAddress}>
                    <FormLabel size="md">Email</FormLabel>
                    <Input
                      id="emailAddress"
                      {...register(`residents.${index}.emailAddress`, {
                        required: 'This is required',
                        pattern: {
                          value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i,
                          message: 'Invalid Email Address',
                        },
                      })}
                      variant={'required-field'}
                      type="email"
                      maxLength={255}
                      onChange={e => {
                        setValue(`residents.${index}.emailAddress`, (e?.target as HTMLInputElement).value)
                        if ((e.target as HTMLInputElement).value?.length > fieldsLimitNumber) {
                          setError(`residents.${index}.emailAddress`, {
                            type: 'custom',
                            message: t(`${NEW_PROJECT}.fieldLimitReached`),
                          })
                        } else {
                          clearErrors(`residents.${index}.emailAddress`)
                        }
                      }}
                    />
                    <FormErrorMessage>{errors?.residents?.[index]?.emailAddress?.message}</FormErrorMessage>
                  </FormControl>
                </GridItem>

                <GridItem>
                  <FormControl>
                    <FormLabel size="md">Phone</FormLabel>

                    <HStack>
                      <FormControl width={'180px'} isInvalid={!!errors?.residents?.[index]?.phoneNumber}>
                        <Controller
                          control={control}
                          {...register(`residents.${index}.phoneNumber`, {
                            required: 'This is required',
                            validate: (value: any) => {
                              if (value.replace(/\D+/g, '').length === 10) return true

                              return false
                            },
                          })}
                          render={({ field }) => {
                            return (
                              <>
                                <NumberFormat
                                  id="phoneNumber"
                                  customInput={Input}
                                  value={field.value}
                                  onChange={e => field.onChange(e)}
                                  format="(###)-###-####"
                                  mask="_"
                                  placeholder="(___)-___-____"
                                  variant={'required-field'}
                                />
                                <FormErrorMessage>
                                  {errors?.residents?.[index]?.phoneNumber &&
                                    errors?.residents?.[index]?.phoneNumber?.message}
                                  {errors?.residents?.[index]?.phoneNumber &&
                                    errors?.residents?.[index]?.phoneNumber!.type === 'validate' && (
                                      <span>Phone number should be a 10-digit number</span>
                                    )}
                                </FormErrorMessage>
                              </>
                            )
                          }}
                        />
                      </FormControl>
                      <Box pos="relative">
                        {index > 0 && (
                          <Box color="barColor.100" fontSize="15px" pos="absolute" top="-15px">
                            <Center>
                              <Icon
                                as={MdOutlineCancel}
                                onClick={() => residentRemove(index)}
                                cursor="pointer"
                                boxSize={5}
                                mt="6px"
                              />
                            </Center>
                          </Box>
                        )}
                      </Box>
                    </HStack>
                  </FormControl>
                </GridItem>
              </Grid>
            </Box>
          )
        })}
      </Box>

      <Flex display="flex" justifyContent="end" borderTop="1px solid #E2E8F0" pt="5" px="6">
        <Button variant="outline" size="md" colorScheme="brand" onClick={props.onClose}>
          {t(`${NEW_PROPERTY}.cancel`)}
        </Button>
        <Button mr="-27px" disabled={isSaveButtonDisabled} colorScheme="brand" size="md" ml="3" type="submit">
          {t(`${NEW_PROPERTY}.save`)}
        </Button>
      </Flex>
    </Flex>
  )
})
